import React from 'react'
import Modal from 'common/Modal'
import CloseIcon from '@material-ui/icons/Close'
import './ConfirmationModal.less'
import { IconButton } from '@material-ui/core'
import Button from '../common/button/Button.component'
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos'
import { breakpoints, useIsMobile } from 'Utilities'
import { COLOR_PRIMARIO } from 'cfgCommon/colors'

/**
 * Objeto de props del componente Footer.
 * @typedef {Object} HeaderProps
 * @property {String} title - Titulo a renderizar en el header.
 * @property {Function} onClose - Funcion a ejecutar al cerrar.
 */

/**
 * Renderiza el footer del modal de confirmación
 * @param {HeaderProps} props
 * @returns {JSX.Element}
 */
function Header(props) {
    const { title, onClose, showBg } = props
    return (
        <div className={`modal--header ${showBg ? 'bg' : ''}`}>
            <p className='modal--header--title'>{title}</p>
            <IconButton onClick={onClose} edge='end'>
                <CloseIcon className='modal-header__close-icon' />
            </IconButton>
        </div>
    )
}

Header.defaultProps = {
    title: 'Titulo',
    onClose: () => {
    }
}

/**
 * Objeto de props del componente Footer.
 * @typedef {Object} FooterProps
 * @property {String} goBackLabel - Label del boton volver.
 * @property {String} cancelLabel - Label del boton cancelar.
 * @property {String} confirmLabel - Label del boton confirmar.
 * @property {Function} onGoBack - Funcion a ejecutar al volver.
 * @property {Function} onCancel - Funcion a ejecutar al cancelar.
 * @property {Function} onConfirm - Funcion a ejecutar al confirmar.
 * @property {Boolean} disabledGoBack - Deshabilita el boton volver.
 * @property {Boolean} disabledCancel - Deshabilita el boton cancelar.
 * @property {Boolean} disabledConfirm - Deshabilita el boton confirmar.
 */

/**
 * Renderiza el footer del modal de confirmación
 * @param {FooterProps} props
 * @returns {JSX.Element}
 */
function Footer(props) {
    const {
        className,
        canGoBack,
        goBackLabel,
        cancelLabel,
        confirmLabel,
        onGoBack,
        onCancel,
        onConfirm,
        disabledGoBack,
        disabledCancel,
        disabledConfirm,
        isLoadingBtnConfirm
    } = props
    const isMobile = useIsMobile()
    const footerClass = `modal--footer ${className}`
    return <div className={footerClass}>
        {canGoBack &&
            <div className='modal--footer--goback'>
                <Button
                    type='tertiary'
                    size='large'
                    label={goBackLabel}
                    onClick={onGoBack}
                    disabled={disabledGoBack}
                />
            </div>
        }
        <div className='modal--footer--actions'>
            {!isMobile && <Button
                type='secondary'
                size='large'
                label={cancelLabel}
                onClick={onCancel}
                disabled={disabledCancel}
            />}
            <Button
                type='primary'
                size='large'
                label={confirmLabel}
                onClick={onConfirm}
                disabled={disabledConfirm}
                isLoading={isLoadingBtnConfirm}
            />
        </div>
    </div>
}

Footer.defaultProps = {
    goBackLabel: 'Volver',
    cancelLabel: 'Cancelar',
    confirmLabel: 'Confirmar',
    onGoBack: () => {
    },
    onCancel: () => {
    },
    onConfirm: () => {
    },
    disabledGoBack: false,
    disabledCancel: false,
    disabledConfirm: false
}

/**
 * Objeto de props del componente ConfirmationModal.
 * @typedef {Object} ConfirmationModalProps
 * @property {JSX.Element} children - Elementos a envolver.
 * @property {String} className - Clase para el contenedor de los elementos recibidos.
 */

/**
 * Renderiza un modal con encabezado y footer con botones confirmar y cancelar
 * @param {FooterProps & HeaderProps & ConfirmationModalProps} props
 */
function ConfirmationModal(props) {
    const {
        children,
        className,
        overlayClassName,
        footerClassName,
        title,
        onClose,
        onConfirm,
        onCancel,
        canGoBack,
        onGoBack,
        goBackLabel,
        cancelLabel,
        confirmLabel,
        disabledGoBack,
        disabledCancel,
        disabledConfirm,
        showHeaderBg,
        isLoadingBtnConfirm,
        modalId,
        shouldCloseOnClickOutside = false,
        hash = '',
        style
    } = props
    const classes = `modal--inner-content ${className}`
    const [isMobile, setIsMobile] = React.useState(false)

    // no se uso el estado de redux, para que lo lea el presupuesto interactivo
    const mqlMobile = window.matchMedia(`(max-width: ${breakpoints.mobileMax}px) and (pointer:coarse)`)
    React.useEffect(() => {
        setIsMobile(mqlMobile.matches)
        mqlMobile.addEventListener('change', () => {
            setIsMobile(mqlMobile.matches)
        })
    }, [isMobile])

    const centerHeader = isMobile && typeof title != 'string'

    React.useEffect(() => {
        if (shouldCloseOnClickOutside) {
            const handleClickOutside = (event) => {
                const modalContent = document.querySelector('.modal--inner')
                if (modalContent && !modalContent.contains(event.target)) {
                    onClose()
                }
            }
            document.addEventListener('mousedown', handleClickOutside)
            return () => {
                document.removeEventListener('mousedown', handleClickOutside)
            }
        }
    }, [shouldCloseOnClickOutside, onClose])

    React.useEffect(() => {
        if (hash !== '') window.location.hash = hash
    }, [])

    return (
        <Modal
            classNames={{ modal: 'modal--inner', overlay: `modal--overlay ${overlayClassName}` }}
            modalId={modalId}
            style={style}
        >
            <Header title={<div className={centerHeader? 'modal--header' : ''}>{isMobile &&
                <ArrowBackIosIcon className='modal-mobile-close-icon' color={'primary'} colorPrimary={COLOR_PRIMARIO}
                                  onClick={onClose} />}{title}</div>}
                    onClose={onClose} showBg={isMobile ? false : showHeaderBg} />
            <div className={classes}>
                {children}
            </div>
            <Footer
                className={footerClassName}
                canGoBack={canGoBack}
                goBackLabel={goBackLabel}
                cancelLabel={cancelLabel}
                confirmLabel={confirmLabel}
                onConfirm={onConfirm}
                onCancel={onCancel}
                onGoBack={onGoBack}
                disabledGoBack={disabledGoBack}
                disabledCancel={disabledCancel}
                disabledConfirm={disabledConfirm}
                isLoadingBtnConfirm={isLoadingBtnConfirm} />
        </Modal>
    )
}

ConfirmationModal.defaultProps = {
    className: '',
    children: '',
    shouldCloseOnClickOutside: false
}

export default ConfirmationModal
